import { useContext, useEffect, useRef, useState } from 'react'
import { Tooltip } from '@mui/material'
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined'

import { AuthContext } from '../../../context/AuthContext'
import { SessionContext } from '../../../context/SessionContext'
import { getOffloadingPanel } from '../../../service/experimentService'
import { conditionTagOf } from '../../session/experiment/experimentConfig'
import { TelemetryContext } from '../context/TelemetryContext'
import { OFFLOADING_EVENTS } from '../constants/telemetryEvents'

// The server labels an exchange a few seconds after the AI's reply is saved (up to ~30s when
// the AI is slow or classmates' analyses are queued); look again then.
const REFRESH_AFTER_REPLY_MS = [3000, 8000, 16000, 35000]

// What "Learn more" sends to the AI tutor, as the student's own message.
export const learnMoreMessage = (concept) =>
  `Can you help me understand "${concept.label}"? Please explain the idea with a small example.`

const chipStyle = {
  fontSize: '0.75rem', color: '#374151', background: '#ffffff',
  border: '1px solid #e5e5e5', borderRadius: '10px', padding: '1px 8px',
  cursor: 'default', outline: 'none',
}

// The site's light cards, not MUI's dark tooltip.
const tooltipSx = {
  bgcolor: '#ffffff', color: '#111827', border: '1px solid #e5e5e5', borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', padding: '10px 12px', maxWidth: 280,
}
const arrowSx = { color: '#ffffff', '&::before': { border: '1px solid #e5e5e5' } }

const learnMoreButton = (disabled) => ({
  marginTop: '8px', padding: '3px 10px', fontSize: '0.75rem', fontFamily: 'inherit',
  color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px',
  cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
})

// One concept: hover (or focus) shows what it is about, an example when the instructor gave
// one, and "Learn more", which asks the AI tutor about it.
const ConceptChip = ({ concept, busy, onView, onLearnMore }) => {
  const [open, setOpen] = useState(false)
  return (
    <Tooltip
      arrow
      placement="top"
      enterDelay={150}
      leaveDelay={150}
      open={open}
      onOpen={() => {
        setOpen(true)
        onView(concept)
      }}
      onClose={() => setOpen(false)}
      componentsProps={{ tooltip: { sx: tooltipSx }, arrow: { sx: arrowSx } }}
      title={
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.8rem', marginBottom: '4px' }}>{concept.label}</div>
          {concept.description && (
            <div style={{ fontSize: '0.78rem', color: '#4b5563', lineHeight: 1.45 }}>{concept.description}</div>
          )}
          {concept.example && (
            <pre style={{
              margin: '6px 0 0', padding: '4px 6px', background: '#f3f4f6', borderRadius: '4px',
              fontFamily: 'Monaco, Menlo, monospace', fontSize: '0.72rem', color: '#374151', whiteSpace: 'pre-wrap',
            }}>
              {concept.example}
            </pre>
          )}
          <button
            type="button"
            disabled={busy}
            title={busy ? 'The AI is still answering' : undefined}
            style={learnMoreButton(busy)}
            onClick={() => {
              setOpen(false)
              onLearnMore(concept)
            }}>
            Learn more
          </button>
        </div>
      }>
      <span tabIndex={0} style={chipStyle}>
        {concept.label}
      </span>
    </Tooltip>
  )
}

// One line above the chat input, for a student in an experiment condition that shows the
// offloading panel: the task's concepts they asked the AI about (the backend's
// offloadingService labels each exchange). Nothing else - no concept list, no progress - and
// nothing at all until there is something to show. Everywhere else it renders nothing and,
// once the server has said so, asks nothing more.
//   isAiProcessing: the chat's own flag; each time it goes back to false a reply has arrived.
//   onAsk(text, { learnMoreConcept }): sends a message to the AI tutor (ChatContext.sendMessage).
export default function OffloadingStrip({ isAiProcessing, onAsk }) {
  const { currentUser } = useContext(AuthContext)
  const { session } = useContext(SessionContext)
  const { logTelemetryEvent } = useContext(TelemetryContext)
  const [concepts, setConcepts] = useState([])
  const [replies, setReplies] = useState(0)
  const enabled = useRef(true)
  const wasProcessing = useRef(false)

  const isStudent = currentUser?.role === 3 || currentUser?.role === 4
  const sessionId = session?.id
  const applies = isStudent && Boolean(conditionTagOf(session))

  useEffect(() => {
    enabled.current = true
    setConcepts([])
    setReplies(0)
  }, [sessionId])

  useEffect(() => {
    if (wasProcessing.current && !isAiProcessing) setReplies((count) => count + 1)
    wasProcessing.current = Boolean(isAiProcessing)
  }, [isAiProcessing])

  useEffect(() => {
    if (!applies || !enabled.current) return
    let cancelled = false
    const load = async () => {
      if (!enabled.current) return
      try {
        const panel = await getOffloadingPanel(sessionId, { refresh: Boolean(currentUser?.uid) })
        if (cancelled) return
        if (!panel.show) {
          enabled.current = false
          setConcepts([])
          return
        }
        setConcepts(panel.concepts)
      } catch (error) {
        // Keep what is shown; the next reply looks again.
      }
    }
    // Once on load; after a reply, again once the server has had time to label it.
    const delays = replies === 0 ? [0] : REFRESH_AFTER_REPLY_MS
    const timers = delays.map((ms) => setTimeout(load, ms))
    return () => {
      cancelled = true
      timers.forEach(clearTimeout)
    }
  }, [applies, sessionId, replies])

  if (!applies || concepts.length === 0) return null

  const view = (concept) =>
    logTelemetryEvent?.(OFFLOADING_EVENTS.CONCEPT_VIEW, { conceptId: concept.id, label: concept.label, timestamp: Date.now() })
  const learnMore = (concept) => {
    if (isAiProcessing || !onAsk) return
    logTelemetryEvent?.(OFFLOADING_EVENTS.LEARN_MORE, { conceptId: concept.id, label: concept.label, timestamp: Date.now() })
    onAsk(learnMoreMessage(concept), { learnMoreConcept: concept.id })
  }

  return (
    <div style={{ padding: '0 12px', background: '#ffffff' }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px',
          padding: '6px 8px', margin: '8px 0 0', borderRadius: '6px',
          background: '#f3f4f6', border: '1px solid #e5e5e5',
        }}>
        <SmartToyOutlined style={{ fontSize: '0.95rem', color: '#737373' }} />
        <span style={{ fontSize: '0.78rem', color: '#4b5563' }}>Asked the AI:</span>
        {concepts.map((concept) => (
          <ConceptChip
            key={concept.id}
            concept={concept}
            busy={Boolean(isAiProcessing)}
            onView={view}
            onLearnMore={learnMore}
          />
        ))}
      </div>
    </div>
  )
}
